import { pdf } from '@react-pdf/renderer';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import InvoiceTemplate from '../templates/pdf/InvoiceTemplate';
import QuotationTemplate, {
  QUOTATION_ACCEPTANCE_CHECKBOX_FIELDS,
  QUOTATION_ACCEPTANCE_TEXT_FIELDS,
  type QuotationPdfCheckboxFieldSpec,
  type QuotationPdfData,
  type QuotationPdfTextFieldSpec,
} from '../templates/pdf/QuotationTemplate';

interface InvoiceData {
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  notes?: string;
  rfiTicketNumber?: string;
  stripePaymentUrl?: string;
  entity?: string;
  senderDisplayName?: string;
  senderAddress?: string;
  senderMobile?: string;
  senderEmail?: string;
  senderPayId?: string;
  senderBsb?: string;
  senderAccountNumber?: string;
  senderAccountName?: string;
  clientName: string;
  companyName?: string;
  clientAddress: string;
  clientMobile?: string;
  clientEmail?: string;
  invoiceDetails?: string;
  projectNumber: string;
  projectLocation: string;
  totalDue: number;
  subtotal: number;
  gstAmount: number;
  paidAmount: number;
  items?: InvoiceItem[];
}

interface InvoiceItem {
  categoryName?: string;
  description: string;
  rate?: number;
  quantity?: number;
  total: number;
  exclGst?: boolean;
}

type QuotationData = QuotationPdfData;

type QuotationAcceptanceCheckboxes = {
  soilReport: boolean;
  legalPointOfDischarge: boolean;
  wsudReport: boolean;
  sewerInformation: boolean;
};

type SignedQuotationAcceptanceData = {
  signedAt: string;
  signedByName: string;
  clientName: string;
  clientCompany?: string;
  clientAbn?: string;
  clientAddress: string;
  clientPhone: string;
  clientEmail: string;
  checkboxes: QuotationAcceptanceCheckboxes;
  signatureDataUrl: string;
};

const A4_PAGE_HEIGHT = 841.89;
const SIGNED_QUOTATION_FIELD_FONT_SIZE = 10;
const activePreviewUrls = new Set<string>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    activePreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    activePreviewUrls.clear();
  });
}

function toPdfBottom(valueFromTop: number, height: number): number {
  return A4_PAGE_HEIGHT - valueFromTop - height;
}

function resolveQuotationAcceptancePage(pages: any[]) {
  // The acceptance/quotation-summary page is rendered as the final explicit page.
  // Earlier flowing content can add pages, so avoid hard-coded page indexes.
  return pages.length > 0 ? pages[pages.length - 1] : null;
}

function openPdfBlobPreview(blob: Blob) {
  const url = URL.createObjectURL(blob);
  const previewWindow = window.open(url, '_blank');

  if (!previewWindow) {
    URL.revokeObjectURL(url);
    throw new Error('The browser blocked the PDF preview window.');
  }

  // Keep the object URL alive so Chrome's built-in PDF viewer can still save/download it later.
  activePreviewUrls.add(url);
}

async function attachQuotationFormFields(baseBlob: Blob): Promise<Blob> {
  const pdfBytes = await baseBlob.arrayBuffer();
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();
  const acceptancePage = resolveQuotationAcceptancePage(pages);

  if (!acceptancePage) {
    return baseBlob;
  }

  const form = pdfDoc.getForm();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  addQuotationFormFields(form, acceptancePage, font);
  form.updateFieldAppearances(font);
  const output = await pdfDoc.save({ updateFieldAppearances: false });
  return new Blob([output], { type: 'application/pdf' });
}

function addQuotationFormFields(form: ReturnType<PDFDocument["getForm"]>, acceptancePage: any, font: any) {
  QUOTATION_ACCEPTANCE_TEXT_FIELDS.forEach((fieldSpec) => {
    const field = form.createTextField(`quotation.${fieldSpec.name}`);
    if (fieldSpec.multiline) {
      field.enableMultiline();
    }
    field.addToPage(acceptancePage, {
      x: fieldSpec.x,
      y: toPdfBottom(fieldSpec.y, fieldSpec.height),
      width: fieldSpec.width,
      height: fieldSpec.height,
      textColor: rgb(0, 0, 0),
      borderColor: rgb(1, 1, 1),
      borderWidth: 0,
      backgroundColor: undefined,
      font,
    });
  });

  QUOTATION_ACCEPTANCE_CHECKBOX_FIELDS.forEach((fieldSpec) => {
    const field = form.createCheckBox(`quotation.${fieldSpec.name}`);
    field.addToPage(acceptancePage, {
      x: fieldSpec.x,
      y: toPdfBottom(fieldSpec.y, fieldSpec.size),
      width: fieldSpec.size,
      height: fieldSpec.size,
      borderColor: rgb(0, 0, 0),
      borderWidth: 0.8,
    });
  });
}

function getQuotationTextField(name: string): QuotationPdfTextFieldSpec {
  const fieldSpec = QUOTATION_ACCEPTANCE_TEXT_FIELDS.find((item) => item.name === name);
  if (!fieldSpec) {
    throw new Error(`Missing quotation PDF text field: ${name}`);
  }
  return fieldSpec;
}

function getQuotationCheckboxField(name: keyof QuotationAcceptanceCheckboxes): QuotationPdfCheckboxFieldSpec {
  const fieldSpec = QUOTATION_ACCEPTANCE_CHECKBOX_FIELDS.find((item) => item.name === name);
  if (!fieldSpec) {
    throw new Error(`Missing quotation PDF checkbox field: ${name}`);
  }
  return fieldSpec;
}

function drawFittingText(
  page: any,
  font: any,
  text: string,
  fieldSpec: QuotationPdfTextFieldSpec,
): void {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim();
  if (!normalized) return;

  let fontSize = SIGNED_QUOTATION_FIELD_FONT_SIZE;
  let output = normalized;
  const maxWidth = Math.max(20, fieldSpec.width - 4);

  while (fontSize > 7 && font.widthOfTextAtSize(output, fontSize) > maxWidth) {
    fontSize -= 0.5;
  }

  if (font.widthOfTextAtSize(output, fontSize) > maxWidth) {
    while (output.length > 4 && font.widthOfTextAtSize(`${output.slice(0, -1)}...`, fontSize) > maxWidth) {
      output = output.slice(0, -1);
    }
    output = `${output}...`;
  }

  page.drawText(output, {
    x: fieldSpec.x + 2,
    y: toPdfBottom(fieldSpec.y, fieldSpec.height) + 2,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  });
}

function drawSignedQuotationTextValues(
  acceptancePage: any,
  font: any,
  textValues: Record<string, string>,
): void {
  Object.entries(textValues).forEach(([name, value]) => {
    const fieldSpec = getQuotationTextField(name);
    drawFittingText(acceptancePage, font, value, fieldSpec);
  });
}

function drawSignedQuotationCheckboxes(
  acceptancePage: any,
  font: any,
  checkboxes: QuotationAcceptanceCheckboxes,
): void {
  (Object.keys(checkboxes) as Array<keyof QuotationAcceptanceCheckboxes>).forEach((name) => {
    if (!checkboxes[name]) return;
    const fieldSpec = getQuotationCheckboxField(name);
    acceptancePage.drawText('X', {
      x: fieldSpec.x + 1.5,
      y: toPdfBottom(fieldSpec.y, fieldSpec.size) + 0.5,
      size: fieldSpec.size - 1,
      font,
      color: rgb(0, 0, 0),
    });
  });
}

async function drawSignatureImage(
  pdfDoc: PDFDocument,
  acceptancePage: any,
  signatureDataUrl: string,
): Promise<void> {
  const signedField = getQuotationTextField('signed');
  const signatureResponse = await fetch(signatureDataUrl);
  const signatureBytes = await signatureResponse.arrayBuffer();
  const signatureImage = signatureDataUrl.includes('image/jpeg') || signatureDataUrl.includes('image/jpg')
    ? await pdfDoc.embedJpg(signatureBytes)
    : await pdfDoc.embedPng(signatureBytes);

  const maxWidth = Math.max(120, signedField.width - 8);
  const maxHeight = 34;
  const scale = Math.min(
    maxWidth / signatureImage.width,
    maxHeight / signatureImage.height,
    1,
  );
  const width = signatureImage.width * scale;
  const height = signatureImage.height * scale;
  const x = signedField.x + 2;
  const y = toPdfBottom(signedField.y, signedField.height) - 2;

  acceptancePage.drawImage(signatureImage, {
    x,
    y,
    width,
    height,
  });
}

async function applySignedQuotationAcceptance(baseBlob: Blob, acceptance: SignedQuotationAcceptanceData): Promise<Blob> {
  const pdfBytes = await baseBlob.arrayBuffer();
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pages = pdfDoc.getPages();
  const acceptancePage = resolveQuotationAcceptancePage(pages);

  if (!acceptancePage) {
    return baseBlob;
  }

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const textValues: Record<string, string> = {
    signed: '',
    signedBy: acceptance.signedByName,
    date: new Date(acceptance.signedAt).toLocaleDateString('en-AU'),
    client: acceptance.clientName,
    company: acceptance.clientCompany || '',
    abn: acceptance.clientAbn || '',
    address: acceptance.clientAddress,
    phone: acceptance.clientPhone,
    email: acceptance.clientEmail,
  };

  drawSignedQuotationTextValues(acceptancePage, font, textValues);
  drawSignedQuotationCheckboxes(acceptancePage, font, acceptance.checkboxes);
  await drawSignatureImage(pdfDoc, acceptancePage, acceptance.signatureDataUrl);
  const output = await pdfDoc.save();
  return new Blob([output], { type: 'application/pdf' });
}

export const pdfService = {
  // Generate and download invoice PDF
  async generateInvoicePDF(data: InvoiceData, filename?: string): Promise<void> {
    try {
      console.log('🔄 Generating invoice PDF...', data);
      
      // Create the PDF document
      const doc = InvoiceTemplate({ data, showPaymentClaim: true });
      
      // Generate PDF blob
      const blob = await pdf(doc).toBlob();
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `Invoice-${data.invoiceNumber}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      console.log('✅ PDF generated and downloaded successfully');
    } catch (error) {
      console.error('❌ Error generating PDF:', error);
      throw new Error(`Failed to generate PDF: ${error}`);
    }
  },

  // Generate PDF blob (for preview or other uses)
  async generateInvoicePDFBlob(data: InvoiceData): Promise<Blob> {
    try {
      const doc = InvoiceTemplate({ data, showPaymentClaim: true });
      return await pdf(doc).toBlob();
    } catch (error) {
      console.error('❌ Error generating PDF blob:', error);
      throw new Error(`Failed to generate PDF blob: ${error}`);
    }
  },

  // Preview PDF in new tab
  async previewInvoicePDF(data: InvoiceData): Promise<void> {
    try {
      const blob = await this.generateInvoicePDFBlob(data);
      openPdfBlobPreview(blob);
    } catch (error) {
      console.error('❌ Error previewing PDF:', error);
      throw new Error(`Failed to preview PDF: ${error}`);
    }
  },

  async generateQuotationPDF(data: QuotationData, filename?: string): Promise<void> {
    try {
      const blob = await this.generateQuotationPDFBlob(data);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `Quotation-${data.quotationNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Error generating quotation PDF:', error);
      throw new Error(`Failed to generate quotation PDF: ${error}`);
    }
  },

  async generateQuotationPDFBlob(data: QuotationData): Promise<Blob> {
    try {
      const doc = QuotationTemplate({ data });
      const baseBlob = await pdf(doc).toBlob();
      return await attachQuotationFormFields(baseBlob);
    } catch (error) {
      console.error('❌ Error generating quotation PDF blob:', error);
      throw new Error(`Failed to generate quotation PDF blob: ${error}`);
    }
  },

  async previewQuotationPDF(data: QuotationData): Promise<void> {
    try {
      const blob = await this.generateQuotationPDFBlob(data);
      openPdfBlobPreview(blob);
    } catch (error) {
      console.error('❌ Error previewing quotation PDF:', error);
      throw new Error(`Failed to preview quotation PDF: ${error}`);
    }
  },

  async generateSignedQuotationPDFBlob(data: QuotationData, acceptance: SignedQuotationAcceptanceData): Promise<Blob> {
    try {
      const doc = QuotationTemplate({ data });
      const baseBlob = await pdf(doc).toBlob();
      return await applySignedQuotationAcceptance(baseBlob, acceptance);
    } catch (error) {
      console.error('❌ Error generating signed quotation PDF blob:', error);
      throw new Error(`Failed to generate signed quotation PDF blob: ${error}`);
    }
  },
};
